import React from "react";
import { STATUS_META, STATUS_ORDER, PRIORITY_META } from "@/lib/constants";
import Avatar from "@/components/Avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { X, CheckCircle2 } from "lucide-react";

// `change(patch, { immediate })` — immediate=false for free-text (debounced by parent),
// immediate=true for dropdowns/dates/pickers (saved on change). `section` picks which
// fields render: "main" (name + description) vs "details" (status, assignees, dates, priority).
export default function TaskForm({ form, change, readOnlyFields, members, checklist, doneCount, isEdit, task, memberMap, section = "all" }) {
  const show = (s) => section === "all" || section === s;

  const toggleAssignee = (uid) => {
    const assigned_to = form.assigned_to.includes(uid)
      ? form.assigned_to.filter((x) => x !== uid)
      : [...form.assigned_to, uid];
    change({ assigned_to }, { immediate: true });
  };

  return (
    <>
      {show("main") && (
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Task Name</label>
          <Input data-testid="task-name-input" value={form.name} disabled={readOnlyFields}
            className="mt-1.5 rounded-sm" placeholder="e.g. Facade Design R01"
            onChange={(e) => change({ name: e.target.value }, { immediate: false })} />
        </div>
      )}

      {show("main") && readOnlyFields && form.description && (
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Description</label>
          <p className="mt-1.5 text-sm text-zinc-700 whitespace-pre-wrap">{form.description}</p>
        </div>
      )}

      {show("main") && !readOnlyFields && (
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Description</label>
          <Textarea data-testid="task-description-input" value={form.description} rows={5}
            className="mt-1.5 rounded-sm" placeholder="Add details… tap a name below to @mention and alert them"
            onChange={(e) => change({ description: e.target.value, description_mentions: form.description_mentions }, { immediate: false })} />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {members.map((m) => {
              const on = form.description_mentions.includes(m.user_id);
              return (
                <button key={m.user_id} type="button" data-testid={`desc-mention-${m.user_id}`}
                  onClick={() => change({
                    description: `${form.description}${form.description && !form.description.endsWith(" ") ? " " : ""}@${m.name.split(" ")[0]} `,
                    description_mentions: on ? form.description_mentions : [...form.description_mentions, m.user_id],
                  }, { immediate: false })}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-500 hover:bg-zinc-50"}`}>
                  @{m.name.split(" ")[0]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {show("details") && (
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Status</label>
          <div className="flex flex-wrap gap-2 mt-1.5" data-testid="task-status-picker">
            {STATUS_ORDER.map((s) => {
              const blocked = s === "completed" && checklist.length > 0 && doneCount < checklist.length;
              return (
                <button key={s} onClick={() => !blocked && change({ status: s }, { immediate: true })}
                  disabled={blocked}
                  title={blocked ? "Tick every checklist item first" : ""}
                  data-testid={`status-option-${s}`}
                  className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors ${form.status === s ? "border-transparent" : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"} ${blocked ? "opacity-40 cursor-not-allowed" : ""}`}
                  style={form.status === s ? { background: STATUS_META[s].bg, color: STATUS_META[s].color } : {}}>
                  {STATUS_META[s].label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {show("details") && isEdit && task?.status === "completed" && task?.completed_by && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid="completion-log">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>Completed by <strong>{memberMap[task.completed_by]?.name || "someone"}</strong>{task.completed_at ? ` · ${new Date(task.completed_at).toLocaleString()}` : ""}</span>
        </div>
      )}

      {show("details") && (
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Assigned To</label>
          <div className="flex flex-wrap gap-2 mt-1.5" data-testid="task-assignee-picker">
            {members.map((m) => {
              const on = form.assigned_to.includes(m.user_id);
              return (
                <button key={m.user_id} onClick={() => toggleAssignee(m.user_id)}
                  data-testid={`assignee-option-${m.user_id}`}
                  className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border text-xs transition-colors ${on ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"}`}>
                  <Avatar id={m.user_id} name={m.name} size={20} />
                  {m.name.split(" ")[0]}
                  {on && <X className="h-3 w-3" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {show("details") && !readOnlyFields && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0">
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Start Date</label>
              <DatePicker testId="task-start-input" value={form.start_date} placeholder="Pick a date"
                onChange={(v) => change({ start_date: v }, { immediate: true })} />
            </div>
            <div className="min-w-0">
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">End Date</label>
              <DatePicker testId="task-end-input" value={form.end_date} placeholder="Pick a date"
                onChange={(v) => change({ end_date: v }, { immediate: true })} />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Priority</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {Object.keys(PRIORITY_META).map((p) => (
                <button key={p} onClick={() => change({ priority: p }, { immediate: true })}
                  className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors ${form.priority === p ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"}`}>
                  {PRIORITY_META[p].label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
